import {
  FEATURE_DEFINITIONS,
  PLAN_DISPLAY,
  PLAN_LIMITS,
  PLAN_ORDER,
  normalizePlanKey,
} from './planFeatures';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const numberOr = (value, fallback) => (
  hasValue(value) && Number.isFinite(Number(value)) ? Number(value) : fallback
);

const runtimeFeatureKey = (key) => {
  const definition = FEATURE_DEFINITIONS.find((feature) => (
    feature.key === key
    || feature.key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()) === key
  ));
  return definition?.key || key;
};

export const normalizeFeatureEntitlements = (entitlements) => {
  if (!entitlements || typeof entitlements !== 'object' || Array.isArray(entitlements)) return {};

  return Object.entries(entitlements).reduce((normalized, [key, value]) => {
    const normalizedKey = runtimeFeatureKey(key);
    if (!(normalizedKey in normalized) || key === normalizedKey) {
      normalized[normalizedKey] = value;
    }
    return normalized;
  }, {});
};

export const getPricingTierKey = (tier) => {
  const candidate = String(tier?.planKey || '').trim().toLowerCase();
  return PLAN_ORDER.includes(candidate) ? candidate : normalizePlanKey(tier?.tierName);
};

export const createCatalogPricingTier = (planKey) => {
  const display = PLAN_DISPLAY[planKey];
  const limits = PLAN_LIMITS[planKey];
  return {
    id: planKey,
    planKey,
    tierName: display.label,
    description: display.description,
    price: display.price,
    annualPrice: display.annualPrice,
    annualMonthlyPrice: display.annualMonthlyPrice,
    isPopular: display.isPopular,
    badgeText: display.badge,
    ctaText: display.ctaText,
    isActive: true,
    sortOrder: PLAN_ORDER.indexOf(planKey) + 1,
    maxForms: limits.maxForms,
    maxSubmissions: limits.maxSubmissions,
    maxTeamMembers: limits.maxTeamMembers,
    maxStorageMb: limits.maxStorageMb,
    featureEntitlements: {},
    features: FEATURE_DEFINITIONS.map((feature, index) => ({
      id: index + 1,
      key: feature.key,
      text: feature.label,
      infoPopup: feature.description,
      included: feature.plans[planKey] !== false,
      ...(typeof feature.plans[planKey] === 'string' ? { value: feature.plans[planKey] } : {}),
    })),
  };
};

export const normalizePricingTier = (tier, planKey = getPricingTierKey(tier)) => {
  const fallback = createCatalogPricingTier(planKey);
  const annualPrice = numberOr(tier?.annualPrice, fallback.annualPrice);
  const annualMonthlyPrice = numberOr(
    tier?.annualMonthlyPrice,
    annualPrice > 0 ? annualPrice / 12 : fallback.annualMonthlyPrice,
  );

  return {
    ...fallback,
    ...tier,
    planKey,
    tierName: tier?.tierName || fallback.tierName,
    description: tier?.description || fallback.description,
    price: numberOr(tier?.price, fallback.price),
    annualPrice,
    annualMonthlyPrice,
    maxForms: numberOr(tier?.maxForms, fallback.maxForms),
    maxSubmissions: numberOr(tier?.maxSubmissions, fallback.maxSubmissions),
    maxTeamMembers: numberOr(tier?.maxTeamMembers, fallback.maxTeamMembers),
    maxStorageMb: numberOr(tier?.maxStorageMb, fallback.maxStorageMb),
    isPopular: typeof tier?.isPopular === 'boolean' ? tier.isPopular : fallback.isPopular,
    isActive: tier?.isActive !== false,
    featureEntitlements: normalizeFeatureEntitlements(tier?.featureEntitlements),
    features: Array.isArray(tier?.features) && tier.features.length > 0
      ? tier.features
      : fallback.features,
  };
};

export const getCanonicalPricingTiers = (tiers = [], { includeMissing = true } = {}) => (
  PLAN_ORDER.map((planKey) => {
    const tier = tiers.find((candidate) => (
      String(candidate?.planKey || '').trim().toLowerCase() === planKey
    ));
    return tier || includeMissing ? normalizePricingTier(tier, planKey) : null;
  }).filter(Boolean)
);